import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { FormBuilder, UntypedFormControl, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { first, isNil, isNumber, last } from 'lodash';
import * as moment from 'moment';
import {
  BehaviorSubject,
  Observable,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  of,
  startWith,
  tap,
  withLatestFrom,
} from 'rxjs';
import { catchError, debounceTime, finalize, shareReplay, switchMap, take } from 'rxjs/operators';

import { UserService } from '@app/services/user.service';
import {
  selectSelectedPortfolioId,
  selectSelectedPropertyIds,
} from '@dashboards/store/selectors/property-selector.selectors';
import { selectActiveUsers } from '@main-application/administration/store/selectors/administration.selectors';
import { allPortfoliosValue } from '@main-application/application-menu/property-selector/constants';
import { InspectionAddBaseFormComponent } from '@main-application/inspections/components/inspection-add-dialog/inspection-add-base-form.component';
import { InspectionFormDefaults } from '@main-application/inspections/components/inspection-add-dialog/inspection-add-dialog.component';
import { InspectionCheckDuplicatesModalComponent } from '@main-application/inspections/components/inspection-check-duplicates-modal/inspection-check-duplicates-modal.component';
import { GetInspectionRemindListConst } from '@main-application/inspections/models/inspection-notify-list.const';
import {
  InspectionRecurrencePeriod,
  InspectionScheduleType,
  RestDynamicInspectionModel,
  RestInspectionCreateModel,
  RestInspectionsModel,
  ReviewRequiredPolicy,
} from '@main-application/inspections/models/rest-inspections-model.interface';
import { InspectionService } from '@main-application/inspections/services/inspection.service';
import { TimezoneService } from '@main-application/management/pages/system-configuration/components/date-time-configuration/timezone.service';
import { selectUserData } from '@main-application/store/selectors/user.selectors';
import { getPropertyUnits } from '@portfolio/store/portfolio.actions';
import {
  selectProperties,
  selectPropertyUnits,
  selectPropertyUnitsLoading,
} from '@portfolio/store/portfolio.selectors';
import { debounceSafeDefault } from '@shared/constants/debounce-time-safe-default.constant';
import { ReviewRequiredPolicyList } from '@shared/constants/inspection-type-list.const';
import { UserType } from '@shared/enums/user-type';
import { getPortfolioProperties } from '@shared/functions/get-portfolio-properties.function';
import { getUserList } from '@shared/functions/get-user-list.function';
import { IRadioButtonOption } from '@shared/interfaces/radio-button-option.interface';
import { RestUnitSummaryModel } from '@shared/interfaces/unit.interface';
import { RestUserModel } from '@shared/interfaces/user.interface';
import { DestroyService } from '@shared/services/destroy.service';
import { LocalCacheService } from '@shared/services/local-cache.service';
import { scrollToIdRef } from '@shared/utils/dom/scroll-to-id.util';
import { isExist } from '@shared/utils/exist.util';
import { isControlInvalid } from '@shared/utils/forms/is-control-invalid';
import { RestTemplateModel } from '@template/models/rest-template-model.interface';
import { selectAvailableTemplates } from '@template/store/template.selectors';
import { SnackbarErrorMessage } from '@ui-components/components/customized-snackbar/snackbar-message.enum';
import { SnackbarService } from '@ui-components/components/customized-snackbar/snackbar.service';

import {
  CompletedId,
  CustomNotificationsDialogComponent,
} from '../custom-notifications-dialog/custom-notifications-dialog.component';
import { CustomUsersIdType } from '../models';

export const OnDueDateNotifyId = 0;
export const CustomNotifyId = 10000;
export const CustomNotifyOption: IRadioButtonOption<number> = {
  value: CustomNotifyId,
  label: 'Custom notifications',
};
export const DontNotifyId = 20000;
export const DontNotifyOption: IRadioButtonOption<number> = {
  value: DontNotifyId,
  label: `Don't notify`,
  sort: -DontNotifyId,
};

export interface InspectionFormPostedResult {
  result: boolean;
  inspection?: RestInspectionsModel | RestDynamicInspectionModel;
}

@UntilDestroy()
@Component({
  selector: 'app-inspection-add-regular-form',
  templateUrl: './inspection-add-regular-form.component.html',
  styleUrls: ['./inspection-add-regular-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DestroyService],
})
export class InspectionAddRegularFormComponent extends InspectionAddBaseFormComponent implements OnInit, OnDestroy {
  private inspectionService: InspectionService = inject(InspectionService);
  private formBuilder: FormBuilder = inject(FormBuilder);
  private snackbarService: SnackbarService = inject(SnackbarService);
  private dialog: MatDialog = inject(MatDialog);

  @Input() saveAction: EventEmitter<void>;
  @Input() declare formDefaults: InspectionFormDefaults;
  @Input() diligenceId: number;
  @Input() declare selectedPortfolio: number;
  @Output() formPostedEvent = new EventEmitter<InspectionFormPostedResult>();
  @Output() inspectionTypeChangedEvent = new EventEmitter<InspectionScheduleType>();
  @Output() formChanged = new EventEmitter<InspectionFormDefaults>();

  notifyOptions = this.createNotifyOptions(GetInspectionRemindListConst('notifyOn'));
  portfolioId: number;
  _diligencePropertyId: number;
  allPortfolios = false;

  @Input() set inspectionType(data: {
    type: InspectionScheduleType;
    propertyId?: number;
    formTypeInitialized?: boolean;
  }) {
    if (data?.propertyId && data?.formTypeInitialized) return;

    const val = data.type;
    this.inspectionType$.next(val);
    this._inspectionType = val;
    if (val === InspectionScheduleType.Standard) {
      this.form.controls.recurrencePeriod.setValue(InspectionRecurrencePeriod.DoNotRepeat);
      this.form.controls.notifyAssignee.setValue(null);
      this.endDatePlaceholder = 'n/a';
      this.form.controls.bulkUnitIds.enable();

      this.form.controls.unitId.disable();
      this.form.controls.unitId.reset();
    } else if (val === InspectionScheduleType.Resident) {
      this.form.controls.unitId.enable();

      this.form.controls.bulkUnitIds.disable();
      this.form.controls.bulkUnitIds.updateValueAndValidity();
      this.form.controls.bulkUnitIds.reset([]);

      this.form.controls.assignee.addValidators(Validators.required);
      this.form.controls.assignee.updateValueAndValidity();
    } else {
      this.form.controls.assignee.removeValidators(Validators.required);
      this.form.controls.assignee.updateValueAndValidity();
    }

    if (data.propertyId) {
      this._diligencePropertyId = data.propertyId;
      this.store.dispatch(getPropertyUnits({ propertyId: data.propertyId }));
      this.onSetUserList(data.propertyId, val);
    } else {
      const { propertyId } = this.form.value;
      if (propertyId) {
        this.store.dispatch(getPropertyUnits({ propertyId }));
        this.onSetUserList(propertyId, val);
      }
    }

    this.cdr.detectChanges();
  }

  _inspectionType: InspectionScheduleType = InspectionScheduleType.None;
  inspectionType$ = new BehaviorSubject(InspectionScheduleType.None);
  inspection: RestInspectionsModel;
  endDatePlaceholder: string;
  userData$ = this.store.select(selectUserData);
  defaultAssigneeOptions$ = this.getDefaultAssignees();
  userRadioButtonOptions$: Observable<IRadioButtonOption<number>[]>;
  getUsersOptionsBase$: Observable<IRadioButtonOption<number>[]>;
  reviewRequiredPolicyList: IRadioButtonOption<number>[] = ReviewRequiredPolicyList;
  reviewRequiredPolicy = ReviewRequiredPolicy;
  isDisplayedNotes = false;
  isNotifyDialogOpen = false;
  dialogTimeout: ReturnType<typeof setTimeout>;

  scheduleAppointmentIsRequiredOptions: IRadioButtonOption<boolean>[] = [
    { value: true, label: 'Required' },
    { value: false, label: 'Not required' },
  ];

  form = this.formBuilder.group({
    propertyId: [null as number, [Validators.required]],
    unitId: [null as number, [Validators.required]],
    bulkUnitIds: [null as number[], [Validators.required]],
    inspectionTemplateId: [null as number, [Validators.required]],
    dueDate: [moment().startOf('day').toDate(), [Validators.required]],
    endDate: [null as Date],
    assignee: [null as number],
    createOrNotifyOn: [DontNotifyId],
    autoExpiredOn: [-1],
    notifyAssignee: [null as boolean],
    recurrencePeriod: [InspectionRecurrencePeriod.DoNotRepeat],
    notifyWhenDoneUserIds: [null as number[]],
    notifyWhenNonDefaultOrNASelectedUserIds: [null as number[]],
    reviewRequired: [this.reviewRequiredPolicy.NotRequired],
    notifyWhenReviewRequired: [null as boolean],
    reviewRequiredByUserId: [null as number],
    scheduleAppointmentIsRequired: [false],
    appointmentConfirmed: [false],
    appointmentNotes: [null as string],
    appointmentDate: [null as Date],
    appointmentTime: [null as Date],
    customNotifyOn: [null as number],
    remindOn: [null as number],
    thenRemindOn: [null as number],
    until: [null as number],
  });

  get propertyId(): UntypedFormControl {
    return this.form.get('propertyId') as UntypedFormControl;
  }

  get assignee(): UntypedFormControl {
    return this.form.get('assignee') as UntypedFormControl;
  }

  get notifyAssignee(): UntypedFormControl {
    return this.form.get('notifyAssignee') as UntypedFormControl;
  }

  get createOrNotifyOn(): UntypedFormControl {
    return this.form.get('createOrNotifyOn') as UntypedFormControl;
  }

  get customNotifyOn(): UntypedFormControl {
    return this.form.get('customNotifyOn') as UntypedFormControl;
  }

  get remindOn(): UntypedFormControl {
    return this.form.get('remindOn') as UntypedFormControl;
  }

  get thenRemindOn(): UntypedFormControl {
    return this.form.get('thenRemindOn') as UntypedFormControl;
  }

  get until(): UntypedFormControl {
    return this.form.get('until') as UntypedFormControl;
  }

  public get isResident(): boolean {
    return this._inspectionType === InspectionScheduleType.Resident;
  }

  public get isShowAppointment(): boolean {
    return this.isResident || this._inspectionType === InspectionScheduleType.Standard;
  }

  get unitId(): UntypedFormControl {
    return this.form.get('unitId') as UntypedFormControl;
  }

  get bulkUnitIds(): UntypedFormControl {
    return this.form.get('bulkUnitIds') as UntypedFormControl;
  }

  users$ = new BehaviorSubject<RestUserModel[]>([]);
  excludedUsers$ = new BehaviorSubject<RestUserModel[]>([]);
  appointments = this.inspectionService.getAppointmentHours();
  assigneeName$ = this.users$.pipe(
    filter(users => users.length > 0),
    debounceTime(debounceSafeDefault),
    map(users => {
      return users.find(user => user.id === this.assignee.value)?.displayName || '';
    })
  );

  DontNotifyId = DontNotifyId;
  OnDueDateNotifyId = OnDueDateNotifyId;
  public usersAssigneeVisible$ = combineLatest([
    this.form.controls.unitId.valueChanges.pipe(startWith(null)),
    this.users$,
  ]).pipe(
    map(([unitId, users]) => ({ unitId, users })),
    map(({ users, unitId }) => {
      if (this._inspectionType === InspectionScheduleType.Resident) {
        return users.filter(user => user?.unitId === unitId);
      }
      return users;
    }),
    shareReplay(1)
  );

  public isDisableAssignee$ = this.usersAssigneeVisible$.pipe(
    map(users => {
      return users?.length === 0 && this.isResident;
    })
  );

  constructor() {
    super();
  }

  ngOnInit(): void {
    this.store
      .select(selectSelectedPortfolioId)
      .pipe(untilDestroyed(this))
      .subscribe(portfolioId => {
        this.allPortfolios = portfolioId === allPortfoliosValue;
        this.portfolioId =
          this.selectedPortfolio && (portfolioId === allPortfoliosValue || this.diligenceId)
            ? this.selectedPortfolio
            : portfolioId;
      })
      .untilDestroyed(this);

    this.propertyUnitsLoading$ = this.store.select(selectPropertyUnitsLoading);
    this.initPropertyIdValueChanges();
    this.initUnitIdsValueChanges();
    this.initActiveUsers();
    this.initAvailableTemplates();
    this.initPropertyIdChanges();
    this.initPropertyUnitsChanges();
    this.initDueDateChanges();
    this.initRecurrentPeriodChanges();
    this.initAssigneeValueChanges('assignee', 'notifyAssignee');
    this.initAssigneeValueChanges('reviewRequiredByUserId', 'notifyWhenReviewRequired');
    this.initNotifyAssignee('notifyAssignee', 'assignee');
    this.initNotifyAssignee('notifyWhenReviewRequired', 'reviewRequiredByUserId');
    this.initCreateOrNotifyOnChanges();
    this.initReviewRequiredValueChanges();
    this.initAppointmentConfirmedChanges();
    this.initSaveAction();
    this.initAssigneeValueChangesNotify();
    this.calculateRecurrenceOptions(new Date());

    this.form.controls.unitId.valueChanges
      .pipe(withLatestFrom(this.isDisableAssignee$), untilDestroyed(this))
      .subscribe(([unitId, isDisabled]) => {
        const isSetAssigneeEmpty = this.isResident && (!unitId || isDisabled);
        if (isSetAssigneeEmpty) this.form.controls.assignee.setValue(null);
      })
      .untilDestroyed(this);

    this.form.valueChanges
      .pipe(
        untilDestroyed(this),
        tap(values => {
          const { propertyId, inspectionTemplateId, endDate } = values;

          this.formChanged.emit({
            propertyId,
            inspectionTemplateId,
            endDate,
          });
        })
      )
      .subscribe()
      .untilDestroyed(this);

    this.userRadioButtonOptions$ = this.inspectionType$.pipe(
      distinctUntilChanged(),
      switchMap(type =>
        this.excludedUsers$.pipe(
          map(users => {
            const isAddResident = this.useAddResidentOptionNotify(type);
            if (isAddResident && this.isResident) {
              return [isAddResident, ...users];
            }
            return users;
          }),
          map(users => getUserList(users))
        )
      )
    );

    this.getUsersOptionsBase$ = this.userRadioButtonOptions$.pipe(
      map(users => {
        return users.filter(user => user?.value !== CustomUsersIdType.Resident);
      })
    );

    this.users$
      .pipe(
        untilDestroyed(this),
        tap(users => {
          const { assignee } = this.form.value;
          if (assignee && !users.some(x => x.id === assignee)) {
            this.form.controls.assignee.reset();
          }
        })
      )
      .subscribe()
      .untilDestroyed(this);

    this.onNotifyWhenDoneControlChange();
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.localCacheService.removeByAssociateKey(this.localCacheAssociateKey);
  }

  save() {
    this.formPosted = true;
    if (isControlInvalid(this.form)) {
      this.formPostedEvent.emit({ result: false });
      this.cdr.detectChanges();
      return;
    }

    const {
      propertyId,
      inspectionTemplateId,
      unitId,
      bulkUnitIds,
      dueDate,
      autoExpiredOn,
      endDate,
      assignee,
      recurrencePeriod,
      notifyAssignee,
      notifyWhenDoneUserIds,
      notifyWhenNonDefaultOrNASelectedUserIds,
      reviewRequired,
      notifyWhenReviewRequired,
      reviewRequiredByUserId,
      scheduleAppointmentIsRequired,
      appointmentConfirmed,
      appointmentNotes,
      appointmentDate,
      appointmentTime,
      customNotifyOn,
      remindOn,
      thenRemindOn,
      until,
    } = this.form.value;

    let { createOrNotifyOn } = this.form.value;
    const isCustomNotification = createOrNotifyOn === CustomNotifyId;
    const dontNotify = createOrNotifyOn === DontNotifyId;
    if (isCustomNotification && isExist(customNotifyOn)) {
      createOrNotifyOn = customNotifyOn;
    }

    let notifyOnDate = null;
    if (createOrNotifyOn !== 0 && createOrNotifyOn && !dontNotify) {
      notifyOnDate = moment(dueDate).add(createOrNotifyOn, 'days').toDate();
    }

    let autoExpiredOnDate = null;
    if (autoExpiredOn === -1) {
      autoExpiredOnDate = null;
    } else if (autoExpiredOn) {
      autoExpiredOnDate = moment(dueDate).add(autoExpiredOn, 'days').endOf('day').toDate();
    } else if (autoExpiredOn === 0) {
      autoExpiredOnDate = new Date(dueDate.getFullYear(), dueDate.getMonth(), dueDate.getDate());
    }

    const validNotifyWhenDoneUserIds = notifyWhenDoneUserIds?.filter(id => isNumber(id) && !isNaN(id) && id > 0) ?? [];
    const validNotifyWhenNonDefaultOrNASelectedUserIds =
      notifyWhenNonDefaultOrNASelectedUserIds?.filter(id => isNumber(id) && !isNaN(id) && id > 0) ?? [];

    const isResidentAssignee = this.users$
      .getValue()
      .some(user => user.id === assignee && user.userGroupType === UserType.Resident);

    const model: RestInspectionCreateModel = {
      recurrencePeriod,
      inspectionTemplateId,
      dueDate,
      notifyAssignee,
      notifyWhenDone: validNotifyWhenDoneUserIds?.length > 0,
      notifyWhenDoneUserIds: validNotifyWhenDoneUserIds.join(','),
      notifyWhenNonDefaultOrNASelectedUserIds: validNotifyWhenNonDefaultOrNASelectedUserIds.join(','),
      createOrNotifyOn: notifyOnDate,
      autoExpiredOn: autoExpiredOnDate,
      endDate: endDate,
      assignee,
      unitId: undefined,
      bulkUnitIds: undefined,
      propertyId: undefined,
      isResident: isResidentAssignee,
      scheduleAppointmentIsRequired,
      appointmentConfirmed,
      notifyResidentWhenDone: notifyWhenDoneUserIds?.includes(CustomUsersIdType.Resident),
      appointmentNotes,
      appointmentDate,
      appointmentTime,
    };

    const isValidInspectionType = [InspectionScheduleType.Standard, InspectionScheduleType.Repeating].includes(
      this._inspectionType
    );

    if (isValidInspectionType && (bulkUnitIds?.length > 1 || bulkUnitIds?.[0] === -1)) {
      const allUnits = bulkUnitIds[0] === -1;
      const isBulk = bulkUnitIds.length > 1;

      if (allUnits) {
        model.propertyId = propertyId;
        model.bulkUnitIds = null;
      }

      if (isBulk) {
        model.propertyId = propertyId;
        model.bulkUnitIds = bulkUnitIds;
      }

      if (!allUnits && !isBulk) {
        model.unitId = bulkUnitIds[0];
      }
    } else {
      const getUnitId = this.form.get('bulkUnitIds')?.value?.[0] ?? this.form.get('unitId')?.value;
      model.unitId = getUnitId;
    }

    model.reviewRequired = reviewRequired;
    model.notifyWhenReviewRequired = notifyWhenReviewRequired;
    model.reviewRequiredByUserId = reviewRequiredByUserId;

    if (this.diligenceId) model.diligenceId = this.diligenceId;

    const updatedFields = Object.keys(this.form.controls).filter(key => {
      const control = this.form.get(key);
      return control && control.dirty;
    });

    if (updatedFields.includes('dueDate') && !updatedFields.includes('createOrNotifyOn')) {
      updatedFields.push('createOrNotifyOn');
    }

    if (updatedFields.includes('createOrNotifyOn')) {
      updatedFields.push('notifyAssignee');
    }
    this.form.controls['remindOn'].touched && updatedFields.push('notifyAgainOn');
    this.form.controls['thenRemindOn'].touched && updatedFields.push('notifyAgainAfterNDays');
    this.form.controls['until'].touched && updatedFields.push('notifyAgainStopAfterNTimes');

    if (isCustomNotification) {
      isExist(remindOn) && (model.notifyAgainOn = moment(dueDate).add(remindOn, 'days').toDate());
      isExist(thenRemindOn) && (model.notifyAgainAfterNDays = thenRemindOn);
      (until === null || until > 0) && (model.notifyAgainStopAfterNTimes = until);
    }

    this._inspectionType === InspectionScheduleType.Resident || this._inspectionType === InspectionScheduleType.Standard
      ? this.checkForDuplicates(model)
      : this.create(model);

    this.cdr.detectChanges();
  }

  public onHandleToogleNotes() {
    this.isDisplayedNotes = !this.isDisplayedNotes;

    if (this.isDisplayedNotes) return scrollToIdRef('inspection-appointment-notes', 1);
  }

  private useSnackbarOnSuccess(type: InspectionScheduleType, template: RestTemplateModel) {
    const name = template?.name ?? 'inspection';

    if (type === InspectionScheduleType.Standard && this.bulkUnitIds?.value?.length > 1) {
      this.snackbarService.success(`Bulk ${name} inspection created`);
      return;
    }

    if (type === InspectionScheduleType.Standard) {
      this.snackbarService.success(`Standard ${name} inspection created`);
    }

    if (type === InspectionScheduleType.Resident) {
      this.snackbarService.success(`Resident ${name} inspection created`);
    }
  }

  checkForDuplicates(model: RestInspectionCreateModel): void {
    let flatProperties: IRadioButtonOption<number>[] = [];

    if (this.userProperties.length > 0) {
      if (Array.isArray(this.userProperties[0])) {
        flatProperties = (this.userProperties as IRadioButtonOption<number>[][]).flat();
      } else {
        flatProperties = this.userProperties as IRadioButtonOption<number>[];
      }
    }
    const propertyName = flatProperties.find(el =>
      el.value === this.propertyId?.value ? +this.propertyId.value : model.propertyId
    )?.label;
    const templateName = this.inspectionTypes.find(el => el.value === model.inspectionTemplateId)?.label;
    const unitName = model.unitId ? this.userUnits.find(el => el.value === model.unitId)?.label : null;

    this.inspectionService
      .checkForDuplicates({
        dueDate: model.dueDate,
        inspectionTemplateId: model.inspectionTemplateId,
        isResident: model.isResident,
        unitIds: model?.unitId ? [model.unitId] : model.bulkUnitIds,
      })
      .subscribe(res => {
        if (!res?.length) {
          this.create(model);
          return;
        }
        this.dialog
          .open(InspectionCheckDuplicatesModalComponent, {
            panelClass: 'check-duplicates-modal-container',
            width: '700px',
            autoFocus: false,
            disableClose: true,
            data: {
              inspections: res,
              templateName,
              propertyName,
              unitName,
              users: this.users$.getValue() || [],
            },
          })
          .afterClosed()
          .pipe(take(1))
          .subscribe(res => {
            if (!res.keepEditing) {
              this.create(model);
            } else {
              this.formPostedEvent.emit({ result: false });
            }
          })
          .untilDestroyed(this);
      })
      .untilDestroyed(this);
  }

  private create(model: RestInspectionCreateModel) {
    this.inspectionService
      .create(model)
      .pipe(
        tap(inspection => {
          const template = this.inspectionTemplates.find(t => t.id === model.inspectionTemplateId);
          if (this._inspectionType === InspectionScheduleType.Standard && model.notifyAssignee) {
            const user = this.userListModel.find(u => u.id === model.assignee);
            this.snackbarService.success(SnackbarErrorMessage.InspectionCreatedAndAssigneeNotified, [
              user?.email,
              user?.displayName,
            ]);
          } else {
            this.useSnackbarOnSuccess(this._inspectionType, template);
          }

          this.formPostedEvent.emit({ inspection: { ...inspection, bulkUnitIds: model.bulkUnitIds }, result: true });
          this.cdr.detectChanges();
        }),
        untilDestroyed(this),
        catchError(err => {
          if (model.bulkUnitIds) {
            const errorMsg = this.snackbarService.getErrorCodeMsg(err) ?? 'Error adding bulk inspection';
            this.snackbarService.error(errorMsg);
          } else {
            const unitName = this.userUnits.find(u => u.value === model.unitId)?.label;
            const errorMsg = this.snackbarService.getErrorCodeMsg(err) ?? `Error adding inspection for ${unitName}`;
            this.snackbarService.error(errorMsg);
          }
          this.formPostedEvent.emit({ result: false });
          this.cdr.detectChanges();
          throw err;
        })
      )
      .subscribe()
      .untilDestroyed(this);
  }

  private initNotifyAssignee(controlName: string, relatedControlName: string): void {
    this.form.controls[controlName].valueChanges
      .pipe(
        distinctUntilChanged(),
        debounceTime(debounceSafeDefault),
        withLatestFrom(
          this.users$,
          this.form.controls[relatedControlName].valueChanges.pipe(
            startWith(this.form.controls[relatedControlName].value)
          )
        ),
        untilDestroyed(this)
      )
      .subscribe(([notifyAssignee, users, assignee]) => {
        if (notifyAssignee && !assignee && users?.length > 0) {
          this.snackbarService.warning('We failed to read your mind. Who should we notify?');
          this.form.patchValue({
            [controlName]: null,
          });
          if (controlName === 'notifyAssignee') {
            this.form.patchValue({
              createOrNotifyOn: DontNotifyId,
            });
          }
        }
      })
      .untilDestroyed(this);
  }

  private initAssigneeValueChanges(controlName: string, relatedControlName: string): void {
    let prevAssignee = controlName === 'assignee' ? this.form.controls['assignee'].value : null;
    this.form.controls[controlName].valueChanges
      .pipe(
        distinctUntilChanged(),
        withLatestFrom(this.form.controls[relatedControlName].valueChanges.pipe(startWith(null))),
        debounceTime(debounceSafeDefault),
        untilDestroyed(this)
      )
      .subscribe(([assignee, notifyAssignee]) => {
        if (controlName === 'reviewRequiredByUserId' && relatedControlName === 'notifyWhenReviewRequired') {
          if (assignee && notifyAssignee === null) {
            this.form.controls[relatedControlName].setValue(true);
          }
        } else {
          if (assignee && notifyAssignee === null) {
            this.form.controls[relatedControlName].setValue(true);
          }
        }
        if (!assignee) {
          this.form.patchValue({
            [relatedControlName]: null,
          });
        }
        if (controlName === 'assignee') {
          const prevNotifyValue = this.form.controls['createOrNotifyOn'].value;
          const assigneeChanged = assignee ? !prevAssignee : !!prevAssignee;
          const notifyChanged = assignee ? prevNotifyValue === DontNotifyId : prevNotifyValue !== DontNotifyId;
          const shouldUpdate = assigneeChanged && notifyChanged;
          shouldUpdate &&
            this.form.patchValue({
              createOrNotifyOn: assignee ? OnDueDateNotifyId : DontNotifyId,
            });
          prevAssignee = assignee;
        }

        if (controlName === 'reviewRequiredByUserId') {
          if (assignee && this.form.get('reviewRequired')?.value === ReviewRequiredPolicy.NotRequired) {
            this.form.get('reviewRequired').setValue(ReviewRequiredPolicy.Always);
          }
        }
      })
      .untilDestroyed(this);
  }

  private initAssigneeValueChangesNotify(): void {
    this.form.controls['assignee'].valueChanges
      .pipe(
        distinctUntilChanged(),
        withLatestFrom(this.usersAssigneeVisible$),
        debounceTime(debounceSafeDefault),
        untilDestroyed(this)
      )
      .subscribe(([assignee, users]) => {
        const isCurrentAssigneeResident = users?.find(u => u.id === assignee)?.userGroupType === UserType.Resident;
        const isCurrentAssigneeResidentDynamic = assignee === CustomUsersIdType.Resident;

        const isUpdateNotifyControl = isCurrentAssigneeResident || isCurrentAssigneeResidentDynamic;

        const isAddResident = this.useAddResidentOptionNotify(this._inspectionType);
        const getControl = this.form.controls['notifyWhenDoneUserIds'];
        const isSeetedValueToControl = getControl?.value?.includes(CustomUsersIdType.Resident);

        if (isUpdateNotifyControl && isAddResident && !isSeetedValueToControl) {
          this.form.controls['notifyWhenDoneUserIds'].setValue([
            ...(getControl?.value ?? []),
            CustomUsersIdType.Resident,
          ]);
        }
        this.cdr.detectChanges();
      })
      .untilDestroyed(this);
  }

  private initCreateOrNotifyOnChanges(): void {
    let prevNotifyValue = this.form.controls['createOrNotifyOn'].value;
    this.form.controls['createOrNotifyOn'].valueChanges
      .pipe(distinctUntilChanged(), debounceTime(100))
      .subscribe(res => {
        const prevValue = this.form.controls['notifyAssignee'].value;
        const newValue = res !== DontNotifyId;
        const changed = prevValue !== newValue;
        if (changed) {
          this.form.controls['notifyAssignee'].setValue(newValue);
        }
        if (this.dialogTimeout && res !== CustomNotifyId) {
          clearTimeout(this.dialogTimeout);
          prevNotifyValue = res;
          this.isNotifyDialogOpen = false;
          return;
        }
        if (res === CustomNotifyId) {
          this.openCustomNotificationsDialog(prevNotifyValue, 600);
        }
        prevNotifyValue = res;
      })
      .untilDestroyed(this);
  }

  private initRecurrentPeriodChanges(): void {
    this.form.controls.recurrencePeriod.valueChanges
      .pipe(
        tap(recurrencePeriod => {
          if (this.diligenceId) {
            this.snackbarService.warning('Cant use Repeat option for Diligence inspections');
            this.form.controls.recurrencePeriod.setValue(InspectionRecurrencePeriod.DoNotRepeat, { emitEvent: false });
            return;
          }

          recurrencePeriod !== InspectionRecurrencePeriod.DoNotRepeat
            ? this.inspectionTypeChangedEvent.emit(InspectionScheduleType.Repeating)
            : this.inspectionTypeChangedEvent.emit(InspectionScheduleType.Standard);
        }),
        untilDestroyed(this)
      )
      .subscribe()
      .untilDestroyed(this);
  }

  private initUnitIdsValueChanges(): void {
    this.bulkUnitIds.valueChanges
      .pipe(
        tap(bulkUnitIds => {
          if (bulkUnitIds?.length > 1) {
            if (last(bulkUnitIds) === -1) {
              this.bulkUnitIds.setValue([-1]);
            } else {
              if (bulkUnitIds.includes(-1)) {
                this.bulkUnitIds.setValue(bulkUnitIds.filter(i => i > 0));
              }
            }
          }
        }),
        untilDestroyed(this)
      )
      .subscribe()
      .untilDestroyed(this);
  }

  private initPropertyIdChanges(): void {
    combineLatest([this.store.select(selectSelectedPropertyIds), this.store.select(selectProperties)])
      .pipe(
        filter(([, userProperties]) => !!userProperties),
        tap(([propertyIds, userProperties]) => {
          this.userProperties = getPortfolioProperties(
            userProperties.filter(p => this.portfolioId === allPortfoliosValue || p.portfolioId === this.portfolioId)
          );
          const userPropertyIds = this.userProperties.map(el => el.value);

          if (this.diligenceId && this._diligencePropertyId) {
            this.propertyId.setValue(this._diligencePropertyId);
          } else {
            if (!this.formDefaults.propertyId) {
              if (propertyIds?.length > 0) {
                const firstMatch =
                  userPropertyIds?.length && this.allPortfolios ? first(userPropertyIds) : first(propertyIds);
                this.propertyId.setValue(firstMatch);
              }
              if (!this.propertyId.value && this.userProperties?.length === 1) {
                this.propertyId.setValue(this.userProperties[0].value);
              }
            }
          }

          this.cdr.detectChanges();
        }),
        untilDestroyed(this)
      )
      .subscribe()
      .untilDestroyed(this);
  }

  private initPropertyUnitsChanges(): void {
    this.store
      .select(selectPropertyUnits)
      .pipe(
        filter((propertyUnits: RestUnitSummaryModel[]) => !!propertyUnits),
        filter(() => !!this.propertyId.value),
        tap((unitList: RestUnitSummaryModel[]) => {
          this.userUnits = this.getUnitList(
            unitList,
            this._inspectionType === InspectionScheduleType.Repeating ||
              this._inspectionType === InspectionScheduleType.Standard
          );
          this.cdr.markForCheck();
        }),
        untilDestroyed(this)
      )
      .subscribe()
      .untilDestroyed(this);
  }

  private initAvailableTemplates(): void {
    this.store
      .select(selectAvailableTemplates)
      .pipe(
        filter(templates => !!templates),
        tap(templates => {
          this.inspectionTemplates = templates;
          this.filterOutInspectionTypes();
          this.form.patchValue(this.formDefaults);
          this.cdr.detectChanges();
        }),
        untilDestroyed(this)
      )
      .subscribe()
      .untilDestroyed(this);
  }

  private initPropertyIdValueChanges(): void {
    this.propertyId.valueChanges
      .pipe(
        distinctUntilChanged(),
        tap(propertyId => {
          if (!isNil(propertyId)) {
            this.store.dispatch(getPropertyUnits({ propertyId }));
            this.onSetUserList(propertyId, this._inspectionType);
          }
          this.unitId.reset();
          this.bulkUnitIds.reset();
        }),
        untilDestroyed(this)
      )
      .subscribe()
      .untilDestroyed(this);
  }

  private initDueDateChanges(): void {
    this.form.controls.dueDate.valueChanges
      .pipe(
        tap(dueDate => {
          this.calculateRecurrenceOptions(dueDate);
          this.cdr.detectChanges();
        }),
        untilDestroyed(this)
      )
      .subscribe()
      .untilDestroyed(this);
  }

  private initActiveUsers(): void {
    combineLatest([this.store.select(selectActiveUsers), this.store.select(selectUserData)])
      .pipe(
        untilDestroyed(this),
        filter(([users, userData]) => !!users && !!userData)
      )
      .subscribe(
        ([users, userData]) =>
          (this.userListModel = users.map(u => ({
            ...u,
            isMe: u.id === userData.id,
          })))
      )
      .untilDestroyed(this);
  }

  private initSaveAction(): void {
    this.saveAction
      .pipe(
        tap(() => this.save()),
        untilDestroyed(this)
      )
      .subscribe()
      .untilDestroyed(this);
  }

  private getDefaultAssignees(): Observable<number[]> {
    return combineLatest([this.userData$]).pipe(
      map(([userData]) => {
        const result = [];
        if (userData) {
          result.push(userData.id);
        }
        return result;
      })
    );
  }

  private onSetUserList(propertyId: number, inspectionType?: InspectionScheduleType): void {
    const useCheckType = inspectionType === InspectionScheduleType.Resident;
    const userTypes = useCheckType ? [UserType.Resident] : [];

    const getBaseUsers$ = useCheckType ? this.initUserList(propertyId).pipe(take(1)) : of([]);

    const useFeatchUsers$ = combineLatest([this.initUserList(propertyId, userTypes).pipe(take(1)), getBaseUsers$]);

    useFeatchUsers$
      .pipe(untilDestroyed(this))
      .subscribe(([userList, baseUserList]) => {
        this.users$.next(userList);

        const getResult = useCheckType ? baseUserList : userList;

        this.excludedUsers$.next([...getResult]);
      })
      .untilDestroyed(this);
  }

  private initReviewRequiredValueChanges(): void {
    this.form
      .get('reviewRequired')
      .valueChanges.pipe(untilDestroyed(this), distinctUntilChanged())
      .subscribe(value => {
        if (value === this.reviewRequiredPolicy.NotRequired) {
          this.form.get('reviewRequiredByUserId').setValue(null);
          this.form.get('reviewRequiredByUserId').clearValidators();
        } else {
          this.form.get('reviewRequiredByUserId').setValidators([Validators.required]);
        }

        this.form.get('reviewRequiredByUserId').updateValueAndValidity();
      })
      .untilDestroyed(this);
  }

  private initAppointmentConfirmedChanges(): void {
    this.form
      .get('appointmentConfirmed')
      .valueChanges.pipe(untilDestroyed(this), distinctUntilChanged())
      .subscribe(value => {
        const appointmentDateControl = this.form.get('appointmentDate');
        const appointmentTimeControl = this.form.get('appointmentTime');

        if (value) {
          appointmentDateControl.setValidators([Validators.required]);
          if (this.isShowAppointment) appointmentTimeControl.setValidators([Validators.required]);
        } else {
          appointmentDateControl.clearValidators();
          if (this.isShowAppointment) appointmentTimeControl.clearValidators();
        }

        appointmentDateControl.updateValueAndValidity();
        if (this.isShowAppointment) appointmentTimeControl.updateValueAndValidity();

        this.cdr.detectChanges();
      })
      .untilDestroyed(this);
  }

  private onNotifyWhenDoneControlChange(): void {
    this.form
      .get('notifyWhenDoneUserIds')
      .valueChanges.pipe(debounceTime(debounceSafeDefault), untilDestroyed(this))
      .subscribe(ids => {
        const assigneeId = this.form.get('assignee')?.value;
        const isResidentAssignee = this.users$
          .getValue()
          .some(user => user.id === assigneeId && user.userGroupType === UserType.Resident);

        if (assigneeId && (this.isResident || isResidentAssignee) && !ids.includes(CustomUsersIdType.Resident)) {
          this.form.get('notifyWhenDoneUserIds').setValue([...ids, CustomUsersIdType.Resident], { emitEvent: false });
          this.snackbarService.warning('Resident will always receive a copy of Inspection Report');
        }
      })
      .untilDestroyed(this);
  }

  private createNotifyOptions(notifyOptions: IRadioButtonOption<number>[]): IRadioButtonOption<number>[][] {
    return [[CustomNotifyOption], [DontNotifyOption, ...notifyOptions]];
  }

  openCustomNotificationsDialog(prevValue?: number, delay = 0): void {
    if (this.isNotifyDialogOpen) {
      return;
    }
    this.isNotifyDialogOpen = true;
    this.dialogTimeout = setTimeout(() => {
      this.dialog
        .open(CustomNotificationsDialogComponent, {
          panelClass: 'modal-container-without-padding',
          width: '384px',
          autoFocus: false,
          data: {
            showCreateField: false,
            dueDate: this.form.get('dueDate').value,
            notifyOn: this.form.get('customNotifyOn').value,
            remindOn: this.form.get('remindOn').value,
            thenRemindOn: this.form.get('thenRemindOn').value,
            until: this.form.get('until').value,
          },
        })
        .afterClosed()
        .pipe(
          take(1),
          finalize(() => (this.isNotifyDialogOpen = false))
        )
        .subscribe(res => {
          if (!res) {
            isExist(prevValue) && this.form.get('createOrNotifyOn').setValue(prevValue);
            return;
          }

          if (isExist(res.notifyOn)) {
            const value = res.notifyOn === DontNotifyId ? null : res.notifyOn;
            this.form.get('customNotifyOn').setValue(value);
            value === null && this.form.get('createOrNotifyOn').setValue(DontNotifyId);
            this.form.get('createOrNotifyOn').markAsDirty();
          }
          if (isExist(res.remindOn)) {
            const value = res.remindOn === DontNotifyId ? null : res.remindOn;
            this.form.get('remindOn').setValue(value);
            this.form.get('remindOn').markAsTouched();
          }
          if (isExist(res.thenRemindOn)) {
            const value = res.thenRemindOn === DontNotifyId ? null : res.thenRemindOn;
            this.form.get('thenRemindOn').setValue(value);
            this.form.get('thenRemindOn').markAsTouched();
          }
          if (isExist(res.until)) {
            const value = res.until === CompletedId ? null : res.until;
            this.form.get('until').setValue(value);
            this.form.get('until').markAsTouched();
          }
        })
        .untilDestroyed(this);
    }, delay);
  }
}
